import { getDateFormatted } from '@/common/components/FiltersSection/helpers'
import { timeRangeValues } from '@/common/components/TimeRangeSelect/constants'

import {
  defaultPerformanceInsightsPeriod,
  performanceInsightsContentStatuses,
  performanceInsightsPeriodOptions,
  performanceInsightsTabs,
  performanceInsightsTabValues,
} from './constants'

export const getActiveTabValue = pathname => {
  const activeTab = performanceInsightsTabs.find(tab => pathname.startsWith(tab.path))

  if (!activeTab) return performanceInsightsTabValues.global

  return activeTab.value
}

const parseLocalDate = value => {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value)

  if (!match) return null

  const [year, month, day] = match.slice(1).map(Number)
  const date = new Date(year, month - 1, day)

  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null

  return date
}

const formatIsoDate = date => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

const getTwelveMonthsStart = cap => new Date(cap.getFullYear(), cap.getMonth() - 11, 1)

const getLatestDate = (date, dataAvailableFrom) => {
  const dataStartDate = parseLocalDate(dataAvailableFrom)

  if (dataStartDate > date) return dataStartDate

  return date
}

const isRangeWithinLimits = (startDate, endDate, limits) => {
  if (!startDate || !endDate) return false

  return startDate <= endDate && startDate >= limits.minDate && endDate <= limits.maxDate
}

const getPeriodRange = (period, cap) => {
  if (period.timeRange === timeRangeValues.custom) {
    return { startDate: parseLocalDate(period.startDate), endDate: parseLocalDate(period.endDate) }
  }

  if (period.timeRange === timeRangeValues.lastYear) {
    return { startDate: getTwelveMonthsStart(cap), endDate: cap }
  }

  return { startDate: new Date(cap.getFullYear(), cap.getMonth(), cap.getDate() - 29), endDate: cap }
}

export const getPeriodCap = today => new Date(today.getFullYear(), today.getMonth(), 0)

export const getPeriodLimits = (cap, dataAvailableFrom) => {
  return { minDate: getLatestDate(getTwelveMonthsStart(cap), dataAvailableFrom), maxDate: cap }
}

export const getValidPeriodFromSearch = (searchParams, limits) => {
  const timeRange = searchParams.get('timeRange')

  if (performanceInsightsPeriodOptions.some(option => option.value === timeRange)) return { timeRange }
  if (timeRange !== timeRangeValues.custom) return defaultPerformanceInsightsPeriod

  const startDate = parseLocalDate(searchParams.get('startDate'))
  const endDate = parseLocalDate(searchParams.get('endDate'))

  if (!isRangeWithinLimits(startDate, endDate, limits)) return defaultPerformanceInsightsPeriod

  return { timeRange, startDate: getDateFormatted(startDate), endDate: getDateFormatted(endDate) }
}

export const getPeriodParams = (period, cap, dataAvailableFrom) => {
  const { startDate, endDate } = getPeriodRange(period, cap)

  return { start_date: formatIsoDate(getLatestDate(startDate, dataAvailableFrom)), end_date: formatIsoDate(endDate) }
}

export const isSalesDataActivated = returnPortalFeatures => returnPortalFeatures?.sales_orders_data_activated === true

export const getPerformanceInsightsContentStatus = ({
  returnPortalFeatures,
  periodParams,
  availability,
  fetchingAvailability,
  availabilityError,
}) => {
  if (!returnPortalFeatures) return performanceInsightsContentStatuses.loading
  if (!isSalesDataActivated(returnPortalFeatures)) return performanceInsightsContentStatuses.empty
  if (fetchingAvailability) return performanceInsightsContentStatuses.loading
  if (periodParams.start_date > periodParams.end_date) return performanceInsightsContentStatuses.empty
  if (availabilityError) return performanceInsightsContentStatuses.ready
  if (!availability) return performanceInsightsContentStatuses.loading
  if (!availability.has_sales || !availability.data_available_from) return performanceInsightsContentStatuses.empty

  return performanceInsightsContentStatuses.ready
}
